/**
 * Free gifts: keeps the cart in sync with what the discount functions will make free.
 *
 * Shopify Functions cannot add merchandise to a cart - `CartOperation` only offers
 * productDiscountsAdd, orderDiscountsAdd and the entered-code accept/reject pair. So the
 * split is: this file decides what is IN the cart, the functions decide what is FREE.
 *
 * THE ONE INVARIANT: the condition below must match the functions exactly. If this file
 * adds a gift the functions won't discount, the customer is charged for something the
 * page called free. That is the only way this can cost money, so the condition is stated
 * once, here, and mirrors:
 *
 *   bogo-with-gifts          gifts once >= 2 showerheads (any purchase type)
 *   gift-with-subscription   gifts on >= 1 showerhead bought on a selling plan
 *
 * Adding gifts as ONE-TIME lines is also load-bearing: subscriptions renew only lines
 * carrying a selling plan, so a one-time gift simply doesn't exist on cycle 2. Both
 * functions additionally refuse to discount a gift line that has a selling plan.
 */
(function () {
  'use strict';

  /* Products that count toward a BOGO pair AND earn the gifts.
     Quantities POOL across them: 1 showerhead + 1 handheld is a pair. */
  var QUALIFYING_PRODUCT_IDS = [
    8079741747377, // Afina Filtered Shower Head
    8961604714673, // Afina Handheld Filtered Showerhead
  ];

  /** Gifts are a US-market offer. The functions enforce this too - this half only
      keeps non-US carts clean, so nobody sees a gift appear and then get charged. */
  var GIFT_MARKETS = ['US'];

  /** Remembering a manual removal is per-tab and deliberately not persisted. */
  var REMOVED_KEY = 'afina:gifts-removed';

  var inFlight = false;

  /** Shopify.routes.root carries the market/locale prefix. Fall back rather than throw. */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Dawn declares `const PUB_SUB_EVENTS` at the top level of a classic script. That
   * creates a global LEXICAL binding, which is reachable by name but is NOT a property
   * of window - so `window.PUB_SUB_EVENTS` is undefined and any guard written against
   * it silently never fires.
   */
  function pubSubEvents() {
    try {
      if (typeof PUB_SUB_EVENTS !== 'undefined') return PUB_SUB_EVENTS;
    } catch (e) {}
    return window.PUB_SUB_EVENTS || null;
  }

  function log() {
    if (window.AFINA_GIFTS_DEBUG) console.log.apply(console, ['[free-gifts]'].concat([].slice.call(arguments)));
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function inGiftMarket() {
    var country = window.Shopify && window.Shopify.country;
    return GIFT_MARKETS.indexOf(country) !== -1;
  }

  function hasId(list, id) { return list.indexOf(id) !== -1; }

  /**
   * Mirrors the discount functions. Keep in step with them or gifts get charged for.
   *
   * Gifts require a qualifying product ON A SELLING PLAN, whichever discount ends up
   * paying for them:
   *   gift-with-subscription        subscription, no quantity requirement
   *   bogo (gifts.requireSubscription) BOGO earned AND a subscription in the pair
   *
   * The BOGO itself is not subscription-gated - a one-time pair still gets its free
   * unit - but it brings no gifts, so quantity alone must never add them here.
   */
  function qualifies(cart) {
    if (!inGiftMarket()) return false;

    return cart.items.some(function (item) {
      return hasId(QUALIFYING_PRODUCT_IDS, item.product_id) && Boolean(item.selling_plan_allocation);
    });
  }

  function giftLines(cart) {
    var ids = GIFTS.map(function (g) { return g.id; });
    return cart.items.filter(function (item) { return ids.indexOf(item.id) !== -1; });
  }

  function wasRemovedByHand() {
    try { return sessionStorage.getItem(REMOVED_KEY) === '1'; } catch (e) { return false; }
  }

  function rememberRemoval() {
    try { sessionStorage.setItem(REMOVED_KEY, '1'); } catch (e) {}
  }

  function forgetRemoval() {
    try { sessionStorage.removeItem(REMOVED_KEY); } catch (e) {}
  }

  function addGifts(missing) {
    // One request per gift, so an out-of-stock steamer doesn't also lose the holder.
    return Promise.all(missing.map(function (gift) {
      return fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: gift.id, quantity: 1 }] }),
      }).then(function (r) {
        if (!r.ok) log('could not add', gift.name, '- probably out of stock');
        return r.ok;
      }).catch(function () { return false; });
    }));
  }

  function removeLines(lines) {
    // Sequential: /cart/change.js invalidates line keys, so parallel removals race.
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return fetch(root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        }).catch(function () {});
      });
    }, Promise.resolve());
  }

  function reconcile() {
    if (inFlight) return Promise.resolve(false);
    inFlight = true;

    return getCart().then(function (cart) {
      var earned = qualifies(cart);
      var present = giftLines(cart);

      if (!earned) {
        // Gifts left behind after the showerhead is removed would be charged for.
        if (present.length === 0) { forgetRemoval(); return false; }
        log('no longer earned - removing', present.length, 'gift line(s)');
        return removeLines(present).then(function () { forgetRemoval(); return true; });
      }

      // Someone deleted a gift on purpose. Putting it straight back is hostile.
      if (wasRemovedByHand()) return false;

      var presentIds = present.map(function (line) { return line.id; });
      var missing = GIFTS.filter(function (g) { return presentIds.indexOf(g.id) === -1; });

      // A gift bought on a selling plan is not discounted by either function, so drop
      // it back to a plain one-time line rather than letting it be charged.
      var subscribed = present.filter(function (line) { return Boolean(line.selling_plan_allocation); });

      if (missing.length === 0 && subscribed.length === 0) return false;

      return removeLines(subscribed)
        .then(function () { return addGifts(missing.concat(subscribed.map(byId))); })
        .then(function (results) { return results.some(Boolean) || subscribed.length > 0; });
    }).catch(function (e) {
      log('reconcile failed', e);
      return false;
    }).then(function (changed) {
      inFlight = false;
      return changed;
    });
  }

  function byId(line) {
    return GIFTS.filter(function (g) { return g.id === line.id; })[0];
  }

  /** Ask the theme to redraw. Dawn subscribes cart-items and the drawer to this. */
  function refreshUI() {
    var events = pubSubEvents();
    if (typeof window.publish === 'function' && events && events.cartUpdate) {
      return getCart().then(function (cart) {
        window.publish(events.cartUpdate, { source: 'afina-free-gifts', cartData: cart });
      });
    }
    // No pubsub on this page - a reload is heavy-handed but correct.
    if (document.body.classList.contains('template-cart')) window.location.reload();
    return Promise.resolve();
  }

  var pending = null;

  /** Cart writes arrive in bursts; settle before reading. */
  function schedule() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      reconcile().then(function (changed) { if (changed) refreshUI(); });
    }, 150);
  }

  function run() {
    reconcile().then(function (changed) { if (changed) refreshUI(); });
  }

  // Manual removal of a gift is remembered so it isn't re-added under the customer.
  document.addEventListener('click', function (event) {
    var remove = event.target.closest && event.target.closest('cart-remove-button, [data-afina-gift-remove]');
    if (!remove) return;
    var row = remove.closest('[data-afina-gift], .cart-item');
    if (row && row.getAttribute('data-afina-gift') !== null) rememberRemoval();
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  var events = pubSubEvents();
  if (typeof window.subscribe === 'function' && events && events.cartUpdate) {
    window.subscribe(events.cartUpdate, function (event) {
      if (event && event.source === 'afina-free-gifts') return; // don't chase our own tail
      schedule();
    });
  }

  /**
   * Backstop. The PDP is built in Replo and the lander has its own script, neither of
   * which is obliged to publish Dawn's pubsub event. Watching the cart endpoints
   * themselves works whatever framework performed the add.
   */
  (function watchCartRequests() {
    var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    var nativeFetch = window.fetch;
    if (typeof nativeFetch !== 'function') return;

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartWrite = CART_WRITE.test(url);
      return nativeFetch.apply(this, arguments).then(function (response) {
        // inFlight means the write was ours, so reacting would loop.
        if (isCartWrite && !inFlight && response && response.ok) schedule();
        return response;
      });
    };
  })();

  window.AfinaFreeGifts = { reconcile: reconcile, qualifies: qualifies, inGiftMarket: inGiftMarket };
})();
